import debounce from 'lodash/debounce';
import DOMPurify from 'dompurify';
import dispatchMessage from '../helpers/dispatchMessage';
import * as actions from '../helpers/actions';
import * as utils from '../helpers/textEditorUtils';

import type { AttributesType } from './PlainTextEditor.types';

const CONTENT_CHANGE_DELAY = 500;
const CONTENT_CHANGE_MAX_WAIT = 1000;
const FORMATTING_KEYS = ['b', 'i', 'u'];
const KEYS_TO_STOP_PROPAGATION = [' ', 'ArrowLeft', 'ArrowRight'];

class PlainTextEditor {
  private element: HTMLElement;

  private attributes: AttributesType;

  private editorActive: boolean;

  private savedContent: string | null;

  private debouncedHandleContentChange: () => void;

  private boundMouseEnterHandler: () => void;

  private boundMouseLeaveHandler: () => void;

  // eslint-disable-next-line no-unused-vars
  private boundKeyDownHandler: (event: KeyboardEvent) => void;

  // eslint-disable-next-line no-unused-vars
  private boundClickHandler: (event: MouseEvent) => void;

  private boundBlurHandler: () => void;

  // eslint-disable-next-line no-unused-vars
  private boundPasteHandler: (event: ClipboardEvent) => void;

  // eslint-disable-next-line no-unused-vars
  private boundDragOverHandler: (event: DragEvent) => void;

  // eslint-disable-next-line no-unused-vars
  private boundDropHandler: (event: DragEvent) => void;

  constructor(element: HTMLElement, attributes: AttributesType) {
    this.element = element;
    this.attributes = attributes;
    this.editorActive = false;
    this.savedContent = null;

    this.debouncedHandleContentChange = debounce(
      this.handleContentChange.bind(this),
      CONTENT_CHANGE_DELAY,
      {
        leading: false,
        trailing: true,
        maxWait: CONTENT_CHANGE_MAX_WAIT,
      },
    );

    this.boundMouseEnterHandler = this.mouseEnterHandler.bind(this);
    this.boundMouseLeaveHandler = this.mouseLeaveHandler.bind(this);
    this.boundKeyDownHandler = this.handleKeyDown.bind(this);
    this.boundClickHandler = this.handleClick.bind(this);
    this.boundBlurHandler = this.handleBlur.bind(this);
    this.boundPasteHandler = this.handlePaste.bind(this);
    this.boundDragOverHandler = this.handleDragOver.bind(this);
    this.boundDropHandler = this.handleDrop.bind(this);
  }

  init() {
    if (!this.element) return;
    if (!utils.canEditInline(this.element, this.attributes.style)) return;

    this.savedContent = this.getContent();
    this.element.setAttribute('contenteditable', 'true');
    this.element.addEventListener('input', this.debouncedHandleContentChange);
    this.element.addEventListener('paste', this.boundPasteHandler);
    this.element.addEventListener('click', this.boundClickHandler);
    this.element.addEventListener('mouseenter', this.boundMouseEnterHandler);
    this.element.addEventListener('mouseleave', this.boundMouseLeaveHandler);
    this.element.addEventListener('keydown', this.boundKeyDownHandler);
    this.element.addEventListener('blur', this.boundBlurHandler);
    this.element.addEventListener('dragover', this.boundDragOverHandler);
    this.element.addEventListener('drop', this.boundDropHandler);

    utils.initElementAttributes(this.element);
  }

  setEditorActive() {
    this.editorActive = true;
    dispatchMessage(actions.setInlineEditingActive(this.attributes));
    utils.setEditorActiveAttributes(this.element);
  }

  unsetEditorActive() {
    if (this.editorActive) {
      this.editorActive = false;
      dispatchMessage(actions.unsetInlineEditingActive(this.attributes));
    }

    utils.removeEditorActiveAttributes(this.element);
  }

  remove() {
    this.element.removeAttribute('contenteditable');
    this.element.removeEventListener('input', this.debouncedHandleContentChange);
    this.element.removeEventListener('paste', this.debouncedHandleContentChange);
    this.element.removeEventListener('click', this.boundClickHandler);
    this.element.removeEventListener('keydown', this.boundKeyDownHandler);
    this.element.removeEventListener('mouseenter', this.boundMouseEnterHandler);
    this.element.removeEventListener('mouseleave', this.boundMouseLeaveHandler);
    this.element.removeEventListener('blur', this.boundBlurHandler);
    this.element.removeEventListener('dragover', this.boundDragOverHandler);
    this.element.removeEventListener('drop', this.boundDropHandler);

    this.unsetEditorActive();
  }

  handleContentChange() {
    if (!this.editorActive) return;

    const content = this.getContent();
    // Prevent saving the same content multiple times
    if (content === this.savedContent) return;

    dispatchMessage(actions.updateContent({ ...this.attributes, content }));
    this.savedContent = content;
    utils.setContentForSiblingTextElements(this.element, content, this.attributes.style);
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }

    if (event.metaKey && FORMATTING_KEYS.includes(event.key)) {
      event.preventDefault();
    }

    // Prevent native redo/undo actions in favor of theme builder undo/redo
    if ((event.metaKey || event.ctrlKey) && event.key === 'z') {
      event.preventDefault();
    }

    // Prevent JQuery `slick` that is used for carousel from handling space and arrow keys
    if (KEYS_TO_STOP_PROPAGATION.includes(event.key)) {
      event.stopPropagation();
    }
  }

  handleClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.setEditorActive();

    // scroll to the edited settings
    const settingsId = this.element.getAttribute(utils.ELEMENT_SETTINGS_ID_ATTRIBUTE);
    if (settingsId) {
      dispatchMessage(actions.editSetting(settingsId));
    }
  }

  handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedContent = event.clipboardData?.getData('text/plain') || '';
    const sanitizedContent = DOMPurify.sanitize(pastedContent.trim(), { ALLOWED_TAGS: [] });

    const selection = window.getSelection();

    if (!selection) return;

    const range = selection.getRangeAt(0);
    const fragment = range.createContextualFragment(sanitizedContent);

    // Replace the selected content with the pasted content
    range.deleteContents();
    range.insertNode(fragment);

    // Move the cursor to the end of the pasted content
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);

    this.handleContentChange();
  }

  handleBlur() {
    this.handleContentChange();
    this.unsetEditorActive();
  }

  handleDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'none';
    }
    return false;
  }

  handleDrop(event: DragEvent) {
    event.preventDefault();
    return false;
  }

  mouseEnterHandler() {
    utils.setEditorActiveAttributes(this.element);
  }

  mouseLeaveHandler() {
    if (!this.editorActive) {
      utils.removeEditorActiveAttributes(this.element);
    }
  }

  getContent() {
    // convert non-breaking spaces to regular spaces
    return this.element.innerText.replace(/\u00A0/g, ' ');
  }
}

export default PlainTextEditor;
